import { useState, useCallback, useMemo } from "react";
import * as ExcelJS from "exceljs";

interface UseExcelReaderReturn {
  workbook: ExcelJS.Workbook | null;
  sheetIndex: number;
  currentSheet: ExcelJS.Worksheet | null;
  headers: ExcelJS.Row | null;
  rows: ExcelJS.Row[] | null;
  isLoading: boolean;
  error: Error | null;
  readFile: (file: File) => Promise<void>;
  nextSheet: () => void;
  previousSheet: () => void;
  setSheetIndex: (index: number) => void;
  getCell: (rowIndex: number, cellIndex: number) => ExcelJS.Cell | null;
  getRow: (rowIndex: number) => ExcelJS.Row | null;
}

export const useExcelReader = (): UseExcelReaderReturn => {
  const [workbook, setWorkbook] = useState<ExcelJS.Workbook | null>(null);
  const [sheetIndex, setSheetIndexState] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const throwIfNotLoaded = useCallback((): void => {
    if (!workbook) {
      throw new Error("Workbook not loaded");
    }
  }, [workbook]);

  const loadWorkbook = useCallback(async (arrayBuffer: ArrayBuffer): Promise<
    void
  > => {
    try {
      const newWorkbook = new ExcelJS.Workbook();
      await newWorkbook.xlsx.load(arrayBuffer).catch((error) => {
        console.error("Error loading workbook", error);
      });
      setWorkbook(newWorkbook);
      // Find the first non-hidden sheet
      const firstVisibleSheetIndex = newWorkbook.worksheets.findIndex(
        (sheet) => sheet.state === "visible"
      );
      if (firstVisibleSheetIndex !== -1) {
        setSheetIndexState(firstVisibleSheetIndex);
      }
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to load workbook")
      );
    }
  }, []);

  const readFile = useCallback(
    async (file: File): Promise<void> => {
      setIsLoading(true);
      setError(null);
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(file);
      try {
        await new Promise<void>((resolve, reject) => {
          fileReader.onload = async (): Promise<void> => {
            try {
              if (fileReader.result instanceof ArrayBuffer) {
                await loadWorkbook(fileReader.result);
                resolve();
              } else {
                reject(new Error("Failed to read file as ArrayBuffer"));
              }
            } catch (err) {
              reject(err);
            }
          };

          fileReader.onerror = (): void => {
            reject(new Error("Failed to read file"));
          };
        });
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to read file"));
      } finally {
        setIsLoading(false);
      }
    },
    [loadWorkbook]
  );

  const setSheetIndex = useCallback(
    (index: number): void => {
      throwIfNotLoaded();
      if (workbook && index >= 0 && index < workbook.worksheets.length) {
        setSheetIndexState(index);
      }
    },
    [workbook, throwIfNotLoaded]
  );

  const nextSheet = useCallback((): void => {
    setSheetIndex(sheetIndex + 1);
  }, [sheetIndex, throwIfNotLoaded, setSheetIndex]);

  const previousSheet = useCallback((): void => {
    setSheetIndex(sheetIndex - 1);
  }, [sheetIndex, throwIfNotLoaded, setSheetIndex]);

  const currentSheet = useMemo(() => {
    return workbook?.worksheets[sheetIndex] ?? null;
  }, [workbook, sheetIndex]);

  const headers = useMemo(() => {
    return currentSheet?.getRow(1) ?? null;
  }, [currentSheet]);

  const rows = useMemo(() => {
    return currentSheet?.getRows(2, currentSheet?.rowCount ?? 0) ?? null;
  }, [currentSheet]);

  const getCell = useCallback(
    (rowIndex: number, cellIndex: number): ExcelJS.Cell | null => {
      return currentSheet?.getCell(rowIndex, cellIndex) ?? null;
    },
    [currentSheet]
  );

  const getRow = useCallback(
    (rowIndex: number): ExcelJS.Row | null => {
      return currentSheet?.getRow(rowIndex) ?? null;
    },
    [currentSheet]
  );

  return {
    workbook,
    sheetIndex,
    currentSheet,
    headers,
    rows,
    isLoading,
    error,
    readFile,
    nextSheet,
    previousSheet,
    setSheetIndex,
    getCell,
    getRow,
  };
};
